/**
 * Value parsing for the element JSON strings ("1,700 RPM", "-42.3 dBm",
 * "1.2e-9", "LOCKED on JASON-3") and the status vocabulary used by the data
 * items, the derived statuses and the old CONSTANTS.STATUS_LABELS.
 */
import type { ReadingSource, StatusColour, StatusLevel } from '../../types';
import type { Tone } from '@/components/ui/primitives';

export const SOURCE_LABEL: Record<ReadingSource, string> = { json: 'JSON', sim: 'SIM', live: 'LIVE', snapshot: 'SNAPSHOT' };

/** Age tone: fresh within `freshMs`, stale within 5x, else old. */
export function freshnessTone(ageMs: number, freshMs: number): Tone {
  if (ageMs <= freshMs) return 'nominal';
  if (ageMs <= freshMs * 5) return 'caution';
  return 'warning';
}

const NUMBER_TOKEN = /(^|\s)(-?\d[\d,]*(?:\.\d+)?(?:e[-+]?\d+)?)/i;

/** First numeric token at the start of the string or after whitespace, thousands separators removed; null for text values. */
export function parseNumeric(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value !== 'string') return null;
  const m = NUMBER_TOKEN.exec(value.trim());
  if (!m) return null;
  const n = parseFloat(m[2].replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

export function isTextValue(value: unknown): boolean {
  return parseNumeric(value) === null;
}

/** Trailing unit token after the number ("°C", "RPM", "dB/meter", "%"); empty when there is none or it is prose. */
export function extractUnit(value: unknown): string {
  if (typeof value !== 'string') return '';
  const m = NUMBER_TOKEN.exec(value.trim());
  if (!m) return '';
  const rest = value.trim().slice(m.index + m[0].length).trim();
  return /^[a-zA-Z°%/][a-zA-Z°%/0-9]*$/.test(rest) ? rest : '';
}

/** Format a number for display: up to `digits` decimals, integers stay integers, scientific values keep their form. */
export function fmtValue(n: number, digits = 1): string {
  if (!Number.isFinite(n)) return '';
  const abs = Math.abs(n);
  if (abs !== 0 && (abs < 1e-3 || abs >= 1e9)) return n.toExponential(1);
  if (Number.isInteger(n)) return n.toLocaleString('en');
  return n.toLocaleString('en', { maximumFractionDigits: digits, minimumFractionDigits: 0 });
}

const GREEN = new Set(['green', 'operational', 'nominal', 'online', 'ok', 'good', 'normal', 'safe', 'locked', 'active']);
const YELLOW = new Set(['yellow', 'warning', 'degraded', 'marginal', 'stowed', 'acquiring']);
const RED = new Set(['red', 'critical', 'offline', 'failed', 'fault', 'alarm', 'error']);

/** green, yellow or red for a status string; null for bad data such as "100%" or "0.008° error". */
export function statusColourOf(status: unknown): StatusColour | null {
  if (typeof status !== 'string') return null;
  const s = status.trim().toLowerCase();
  if (GREEN.has(s)) return 'green';
  if (YELLOW.has(s)) return 'yellow';
  if (RED.has(s)) return 'red';
  return null;
}

export function toneOf(colour: StatusColour | null | undefined): Tone {
  if (colour === 'green') return 'nominal';
  if (colour === 'yellow') return 'caution';
  if (colour === 'red') return 'warning';
  return 'neutral';
}

/** Short label for a colour (the previous CONSTANTS.STATUS_LABELS), used when no derived level exists. */
export function colourLabel(colour: StatusColour | null | undefined): string {
  if (colour === 'green') return 'OPERATIONAL';
  if (colour === 'yellow') return 'DEGRADED';
  if (colour === 'red') return 'CRITICAL';
  return 'UNKNOWN';
}

export function levelColour(level: StatusLevel): StatusColour {
  if (level === 'OPERATIONAL') return 'green';
  if (level === 'OFFLINE') return 'red';
  return 'yellow';
}

/** Worst of a set of colours (red beats yellow beats green); null when empty. */
export function worstColour(colours: Array<StatusColour | null | undefined>): StatusColour | null {
  let worst: StatusColour | null = null;
  for (const c of colours) {
    if (c === 'red') return 'red';
    if (c === 'yellow') worst = 'yellow';
    else if (c === 'green' && worst === null) worst = 'green';
  }
  return worst;
}

/** Health percentage band colour used by rings and bars: >70 green, >40 yellow, else red. */
export function healthColour(health: number): StatusColour {
  return health > 70 ? 'green' : health > 40 ? 'yellow' : 'red';
}
